/**
 * Estado de UI de la barra lateral: abierta/cerrada y acciones para mutarla.
 *
 * Es puro estado de interfaz local (no hay datos remotos), así que vive en un
 * `useState` y no en TanStack Query (`docs/frontend_architecture.md`, Pr. 4). El
 * valor inicial degrada con elegancia según el ancho de viewport:
 *
 *   - escritorio (≥1024px) → arranca **abierta**;
 *   - tableta/teléfono (<1024px) → arranca **cerrada**;
 *   - sin `window.matchMedia` (jsdom, en tests) → cae a **abierta**, para que la
 *     `nav` esté presente en el árbol accesible por defecto.
 *
 * No persiste en `localStorage` (preferencia volátil; evita fragilidad en tests)
 * ni reacciona al `resize`: el CSS responsivo adapta el aspecto en cada
 * breakpoint conservando la intención del usuario.
 */
import { useCallback, useState } from "react";

/** Umbral de escritorio (Tailwind `lg`). A partir de aquí arranca abierta. */
const DESKTOP_QUERY = "(min-width: 1024px)";

export interface SidebarState {
  /** `true` si la barra lateral está visible. */
  isOpen: boolean;
  /** Alterna entre abierta y cerrada. */
  toggle: () => void;
  /** Cierra la barra lateral (idempotente). */
  close: () => void;
}

/** Resuelve el estado inicial según el viewport, con degradación para jsdom. */
function resolveInitialOpen(): boolean {
  return window.matchMedia?.(DESKTOP_QUERY)?.matches ?? true;
}

export function useSidebar(): SidebarState {
  const [isOpen, setIsOpen] = useState<boolean>(resolveInitialOpen);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  return { isOpen, toggle, close };
}
